/** The review screen as it was before CHANGE_plan_chat, for runs whose pending payload carries no run_plan.
 *  A run that stopped before the deploy is still answerable; nothing new routes here. */
import { Badge, Button, Card } from "@procurement-agents/ui";
import type { SectionPlanReviewPayload, SectionPlanReviewResume } from "@procurement-agents/scope-api";
import { useState } from "react";
import { LevelOfDetail } from "./LevelOfDetail";


const NUMBER = /^(\d+(?:\.\d+)*)([A-Za-z]?)\.?\s+(.+)$/;

type Row = { section_id: string; title: string; parent?: string | null };

const strip = (title: string) => NUMBER.exec(title)?.[3] ?? title;

function renumberTops(titles: string[]): string[] {
  const parsed = titles.map((t) => ({ m: NUMBER.exec(t), t }));
  const parts: string[] = [];
  for (const { m } of parsed) { const head = m?.[1]!.split(".")[0]; if (head && !parts.includes(head)) parts.push(head); }
  const partMap = new Map(parts.map((old, i) => [old, String(i + 1)]));
  const counters = new Map<string, number>();
  return parsed.map(({ m, t }) => {
    if (!m) return t;
    const numbers = m[1]!.split(".");
    const head = partMap.get(numbers[0]!)!;
    if (numbers.length === 1) return `${head}. ${m[3]}`;
    const prefix = [head, ...numbers.slice(1, -1)].join(".");
    const n = (counters.get(prefix) ?? 0) + 1;
    counters.set(prefix, n);
    return `${prefix}.${n} ${m[3]}`;
  });
}

/** Numbering for the older review (runs that stopped before the plan became editable).
 *  The numbering the included sections will carry, with no gaps, and each sub-section numbered under its parent
 *  ("2.3" → "2.3.1"): the same rule the draft and the export apply, so what the reviewer approves is what the
 *  document says. Titles with no leading number are left alone, and so are their sub-sections. */
function renumber(rows: Row[]): string[] {
  const present = new Set(rows.map((r) => r.section_id));
  const isTop = (r: Row) => !r.parent || !present.has(r.parent);
  const tops = rows.filter(isTop);
  const titles = new Map(renumberTops(tops.map((r) => r.title)).map((t, i) => [tops[i]!.section_id, t]));
  const counters = new Map<string, number>();
  return rows.map((r) => {
    if (isTop(r)) return titles.get(r.section_id)!;
    const prefix = NUMBER.exec(titles.get(r.parent!) ?? "")?.[1];
    if (!prefix) return strip(r.title);
    const n = (counters.get(r.parent!) ?? 0) + 1;
    counters.set(r.parent!, n);
    return `${prefix}.${n} ${strip(r.title)}`;
  });
}

type Block = { id: string; kids: string[] };

/** The order as blocks: a section followed by the sub-sections that may never leave it. */
function blocksOf(order: string[], byId: Map<string, Row>): Block[] {
  const present = new Set(order);
  const parentOf = (id: string) => { const p = byId.get(id)?.parent; return p && present.has(p) ? p : null; };
  const blocks: Block[] = [];
  const index = new Map<string, Block>();
  for (const id of order) if (!parentOf(id)) { const b = { id, kids: [] }; blocks.push(b); index.set(id, b); }
  for (const id of order) { const p = parentOf(id); if (p) index.get(p)!.kids.push(id); }
  return blocks;
}

const flatten = (blocks: Block[]) => blocks.flatMap((b) => [b.id, ...b.kids]);

export function LegacyPlanReview({ payload, onSubmit, busy }: { payload: SectionPlanReviewPayload; onSubmit: (value: SectionPlanReviewResume) => void; busy: boolean }) {
  const [include, setInclude] = useState<Record<string, boolean>>(() => Object.fromEntries(payload.sections.map((s) => [s.section_id, s.include])));
  const [focus, setFocus] = useState<Record<string, string>>(() => Object.fromEntries(payload.sections.map((s) => [s.section_id, s.focus ?? ""])));
  const [order, setOrder] = useState<string[]>(() => payload.sections.map((s) => s.section_id));
  const byId = new Map<string, Row>(payload.sections.map((s) => [s.section_id, s]));
  const blocks = blocksOf(order, byId);
  const rows = flatten(blocks).map((id) => payload.sections.find((s) => s.section_id === id)!).filter(Boolean);
  const parentOf = (id: string) => { const p = byId.get(id)?.parent; return p && byId.has(p) ? p : null; };
  // a sub-section is in the document only while its parent is: the server applies the same rule to whatever is sent
  const on = (id: string): boolean => !!include[id] && (parentOf(id) ? on(parentOf(id)!) : true);
  // the numbering follows what is included, live, so dropping a section closes the gap in front of the reviewer
  const numbers = new Map<string, string>();
  const included = rows.filter((s) => on(s.section_id));
  renumber(included).forEach((t, i) => numbers.set(included[i]!.section_id, t));
  const move = (id: string, by: number) => setOrder((o) => {
    const bs = blocksOf(o, byId);
    const parent = parentOf(id);
    if (parent) {   // a sub-section moves among its siblings only
      const kids = bs.find((b) => b.id === parent)!.kids;
      const i = kids.indexOf(id), to = i + by;
      if (to < 0 || to >= kids.length) return o;
      [kids[i], kids[to]] = [kids[to]!, kids[i]!];
    } else {        // a section moves with its sub-sections
      const i = bs.findIndex((b) => b.id === id), to = i + by;
      if (to < 0 || to >= bs.length) return o;
      [bs[i], bs[to]] = [bs[to]!, bs[i]!];
    }
    return flatten(bs);
  });
  const atEdge = (id: string, by: number) => {
    const parent = parentOf(id);
    const list = parent ? blocks.find((b) => b.id === parent)!.kids : blocks.map((b) => b.id);
    const i = list.indexOf(id);
    return i + by < 0 || i + by >= list.length;
  };
  const orderChanged = order.join() !== payload.sections.map((s) => s.section_id).join();
  const submit = () => {
    const inc: string[] = [], exc: string[] = [];
    const foc: Record<string, string | null> = {};
    for (const s of payload.sections) {
      if (on(s.section_id) !== s.include) (on(s.section_id) ? inc : exc).push(s.section_id);
      if ((focus[s.section_id] ?? "") !== (s.focus ?? "")) foc[s.section_id] = focus[s.section_id] || null;
    }
    const edited = inc.length || exc.length || Object.keys(foc).length || orderChanged;
    onSubmit(edited ? { include: inc, exclude: exc, focus: foc, ...(orderChanged ? { order } : {}) } : "approve");
  };
  return (
    <Card title={`Sections proposed for “${payload.template_name}”`}>
      {payload.verbosity && <LevelOfDetail level={payload.verbosity} reason={payload.verbosity_reason} />}
      <ol className="plan">
        {rows.map((s) => {
          const parent = parentOf(s.section_id);
          const droppedWithParent = !!parent && !on(parent);
          return (
            <li key={s.section_id} className={`${on(s.section_id) ? "" : "excluded"}${parent ? " sub" : ""}`}>
              <label className="row">
                <input type="checkbox" checked={on(s.section_id)} disabled={s.required || droppedWithParent} onChange={(e) => setInclude((v) => ({ ...v, [s.section_id]: e.target.checked }))} aria-label={s.title} />
                <strong>{numbers.get(s.section_id) ?? s.title}</strong>{s.required && <Badge>required</Badge>}{s.word_target && on(s.section_id) && <span className="muted small">about {s.word_target} words</span>}
                <span className="plan-move">
                  <button type="button" className="btn quiet small" onClick={() => move(s.section_id, -1)} disabled={busy || atEdge(s.section_id, -1)} aria-label={`Move ${s.title} up`} title="Move up">↑</button>
                  <button type="button" className="btn quiet small" onClick={() => move(s.section_id, 1)} disabled={busy || atEdge(s.section_id, 1)} aria-label={`Move ${s.title} down`} title="Move down">↓</button>
                </span>
              </label>
              <p className="muted small">{droppedWithParent ? `Part of ${payload.sections.find((x) => x.section_id === parent)!.title}, which is not included.` : s.reason}</p>
              {on(s.section_id) && <input className="focus" placeholder="Focus note for drafting (optional)" value={focus[s.section_id] ?? ""} onChange={(e) => setFocus((v) => ({ ...v, [s.section_id]: e.target.value }))} aria-label={`Focus for ${s.title}`} />}
            </li>
          );
        })}
      </ol>
      <Button variant="primary" onClick={submit} disabled={busy}>Approve plan and start drafting</Button>
    </Card>
  );
}
